import { useEffect, useMemo, useState, useCallback } from "react";
import { useAppContext } from "../../context/AppContext";
import { ConstructorTrans, LibraryTrans, ToasterTrans, WorkoutTrans } from "../../utils/translations";
import DropdownButton from "../utilPages/DropdownButton";
import PopupModal from "../utilPages/PopupModal";
import { getFolderPractices } from "../../services/libraryService";
import { hasActiveOnlineClubPass } from "../../utils/communityPassHelpers";
import VideoDisplay from "../utilPages/VideoDisplay";
import SaveForLaterButton from "../utilPages/SaveForLaterButton";
import MarkAsCompleteButton from "../utilPages/MarkAsCompleteButton";
import { updateUser } from "../../services/userService";
import { useSearchParams } from "react-router-dom";
import Loader from "../utilPages/Loader";
import { getVideoThumbnail } from "../../utils/videoThumbnail";

const LibraryCatalogPage = ({
  pageTitle,
  folderId,
  initialLevel = "",
  initialType = "",
  initialDuration = null,
  hideFilters = false,
}) => {
  const { language, token, user, setUser, setToaster, premiumRoles } = useAppContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPractice, setCurrentPractice] = useState({});
  const [practices, setPractices] = useState([]);
  const [selectionStates, setSelectionStates] = useState({
    isComplete: false,
    isSaved: false,
  });
  const [useAlternativePlayer, setUseAlternativePlayer] = useState(false);
  const [isLoadingPractices, setIsLoadingPractices] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  const levelOptions = useMemo(
    () => [
      { key: "", name: WorkoutTrans[language].any },
      { key: "beginner", name: WorkoutTrans[language].beginner },
      { key: "experienced", name: WorkoutTrans[language].experienced },
      { key: "advanced", name: WorkoutTrans[language].advanced },
    ],
    [language]
  );

  const typeOptions = useMemo(
    () => [
      { key: "", name: WorkoutTrans[language].any },
      { key: "recovery", name: WorkoutTrans[language].recovery },
      { key: "progression", name: WorkoutTrans[language].progression },
    ],
    [language]
  );

  const durationOptions = useMemo(
    () => [
      { key: null, name: WorkoutTrans[language].anyFem },
      { key: 15, name: WorkoutTrans[language].fifteen },
      { key: 30, name: WorkoutTrans[language].thirty },
      { key: 60, name: WorkoutTrans[language].sixty },
      { key: 90, name: WorkoutTrans[language].ninety },
    ],
    [language]
  );

  const [selectedLevel, setSelectedLevel] = useState(
    levelOptions.find((option) => option.key === initialLevel) ?? levelOptions[0]
  );
  const [selectedType, setSelectedType] = useState(
    typeOptions.find((option) => option.key === initialType) ?? typeOptions[0]
  );
  const [selectedDuration, setSelectedDuration] = useState(
    durationOptions.find((option) => option.key === initialDuration) ?? durationOptions[0]
  );

  useEffect(() => {
    setSelectedLevel(levelOptions.find((option) => option.key === initialLevel) ?? levelOptions[0]);
    setSelectedType(typeOptions.find((option) => option.key === initialType) ?? typeOptions[0]);
    setSelectedDuration(
      durationOptions.find((option) => option.key === initialDuration) ?? durationOptions[0]
    );
  }, [durationOptions, initialDuration, initialLevel, initialType, levelOptions, typeOptions]);

  const updateUserFavoritesAndCompletions = useCallback(async (practiceId, isSaved, isComplete) => {
    try {
      const updatedUser = await updateUser(token, {
        savedPractices: isSaved
          ? Array.from(new Set([...user.savedPractices, practiceId]))
          : user.savedPractices.filter((id) => id !== practiceId),
        completedPractices: isComplete
          ? Array.from(new Set([...user.completedPractices, practiceId]))
          : user.completedPractices.filter((id) => id !== practiceId),
      });
      setUser(updatedUser);
    } catch (error) {
      console.error("Error updating user:", error);
    }
  }, [token, user, setUser]);

  const handleRestrictedAction = (action) => {
    if (!user) {
      setToaster({ message: ToasterTrans[language].login, type: "info", show: true });
    } else {
      action();
    }
  };

  const toggleSaved = () => {
    setSelectionStates((prev) => {
      const newSavedState = !prev.isSaved;
      updateUserFavoritesAndCompletions(currentPractice.uuid, newSavedState, prev.isComplete);
      return { ...prev, isSaved: newSavedState };
    });
  };

  const toggleComplete = () => {
    setSelectionStates((prev) => {
      const newCompleteState = !prev.isComplete;
      updateUserFavoritesAndCompletions(currentPractice.uuid, prev.isSaved, newCompleteState);
      return { ...prev, isComplete: newCompleteState };
    });
  };

  const openModal = (practice) => {
    setCurrentPractice(practice);
    setUseAlternativePlayer(false);
    setSelectionStates({
      isComplete: user?.completedPractices?.includes(practice.uuid) || false,
      isSaved: user?.savedPractices?.includes(practice.uuid) || false,
    });
    setIsModalOpen(true);
    setSearchParams({ video: practice.uuid });
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectionStates({ isComplete: false, isSaved: false });
    setUseAlternativePlayer(false);
    setCurrentPractice({});
    setSearchParams({});
  };

  useEffect(() => {
    if (!folderId) return;
    let cancelled = false;
    (async () => {
      setIsLoadingPractices(true);
      try {
        const fetchedPractices = await getFolderPractices(folderId, { language, token });
        if (cancelled) return;
        setPractices(fetchedPractices);

        const practiceId = searchParams.get("video");
        if (practiceId) {
          const practiceToOpen = fetchedPractices.find((practice) => practice.uuid === practiceId);
          if (practiceToOpen) {
            openModal(practiceToOpen);
          }
        }
      } finally {
        if (!cancelled) setIsLoadingPractices(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderId, language, token]);

  const filteredPractices = useMemo(() => {
    const hasFullLibraryAccess =
      premiumRoles.includes(user?.role) || hasActiveOnlineClubPass(user);
    const filtered = practices.filter((practice) => {
      const matchesLevel =
        selectedLevel.key === "" || (practice.level && practice.level.includes(selectedLevel.key));
      const matchesType =
        selectedType.key === "" ||
        practice.type === selectedType.key ||
        practice.type.includes(selectedType.key);
      const matchesDuration =
        selectedDuration.key === null ||
        (selectedDuration.key === 15 && practice.duration <= 15) ||
        (selectedDuration.key === 30 && practice.duration > 15 && practice.duration <= 45) ||
        (selectedDuration.key === 60 && practice.duration > 45 && practice.duration <= 75) ||
        (selectedDuration.key === 90 && practice.duration > 75 && practice.duration <= 105);
      const matchesScope = hasFullLibraryAccess || practice.scope === "regular";

      return matchesLevel && matchesType && matchesDuration && matchesScope;
    });
    return filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [practices, selectedLevel, selectedType, selectedDuration, user, premiumRoles]);

  // AppContext's `language` is the literal string "russian"/"english"; video/cover/header/
  // description objects are keyed by the short "ru"/"en" codes - map before indexing, otherwise
  // the [language] lookup below is always undefined and silently falls through to `.ru`.
  const langKey = language === "russian" ? "ru" : "en";

  const getPracticeVideoUrl = (practice) =>
    practice?.videos?.[langKey]?.default ||
    practice?.videos?.ru?.default ||
    practice?.videos?.en?.default ||
    practice?.videoURL ||
    "";

  const getPracticeAlternativeVideoUrl = (practice) =>
    practice?.videos?.[langKey]?.alternative ||
    practice?.videos?.ru?.alternative ||
    practice?.videos?.en?.alternative ||
    practice?.alternativeVideoURL ||
    "";

  const getPracticeCoverPhotoUrl = (practice) =>
    practice?.coverPhotos?.[langKey] ||
    practice?.coverPhotos?.ru ||
    practice?.coverPhotos?.en ||
    "";

  const getPracticeHeader = (practice) =>
    practice?.header?.[langKey] ||
    practice?.header?.ru ||
    practice?.header?.en ||
    "";

  const getPracticeDescription = (practice) =>
    practice?.description?.[langKey] ||
    practice?.description?.ru ||
    practice?.description?.en ||
    "";

  const getActivePracticeVideoUrl = (practice) => {
    const primary = getPracticeVideoUrl(practice);
    const alternative = getPracticeAlternativeVideoUrl(practice);
    return useAlternativePlayer ? (alternative || primary) : primary;
  };

  return (
    <main className="flex min-h-screen flex-col">
      <Loader load={isLoadingPractices} />
      <div className="h-[55vh] w-full overflow-hidden">
        <img
          src="/wallpaper/mat.png"
          alt=""
          className="h-full w-full object-cover object-[center_25%]"
        />
      </div>
      <div className="w-11/12 lg:w-10/12 mx-auto mb-10">
        <h1>{pageTitle ?? LibraryTrans[language].header}</h1>
        {!hideFilters && (
          <div className="flex flex-col sm:flex-row justify-between items-center mt-5 w-11/12 mx-auto">
            <div className="flex flex-wrap gap-4 sm:gap-14 text-xs lg:text-base w-full justify-around md:justify-start">
              <DropdownButton
                title={WorkoutTrans[language].level}
                options={levelOptions}
                selectedOption={selectedLevel}
                setSelectedOption={setSelectedLevel}
              />
              <DropdownButton
                title={WorkoutTrans[language].type}
                options={typeOptions}
                selectedOption={selectedType}
                setSelectedOption={setSelectedType}
              />
            </div>
            <div className="mt-4 sm:mt-0 text-xs lg:text-base">
              <DropdownButton
                title={WorkoutTrans[language].duration}
                options={durationOptions}
                selectedOption={selectedDuration}
                setSelectedOption={setSelectedDuration}
              />
            </div>
          </div>
        )}
        <div className="container mx-auto w-11/12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 mt-8">
            {filteredPractices.map((practice, index) => {
              const header = getPracticeHeader(practice);
              const coverImage = getPracticeCoverPhotoUrl(practice) || getVideoThumbnail(getPracticeVideoUrl(practice));
              return (
                <div key={practice.uuid || index} className="flex flex-col">
                  <img
                    src={coverImage}
                    alt={header || `Video thumbnail ${index + 1}`}
                    onClick={() => openModal(practice)}
                    className="cursor-pointer m-auto rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200"
                  />
                  {header && (
                    <button
                      type="button"
                      onClick={() => openModal(practice)}
                      className="mt-3 text-sm sm:text-base font-medium text-darkBrown text-center line-clamp-2 hover:text-brown transition-colors"
                    >
                      {header}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          {!premiumRoles.includes(user?.role) && !hasActiveOnlineClubPass(user) ? (
            <p className="mt-20 text-center text-sm text-darkBrown pointer">
              <button
                className="underline"
                onClick={() => window.location.assign("/onlineClub")}
              >
                {LibraryTrans[language].becomeMember}
              </button>
            </p>
          ) : (
            <p className="mt-20"></p>
          )}
        </div>
      </div>
      {isModalOpen && (
        <PopupModal onClose={closeModal}>
          <div className="flex flex-col h-full">
            {(getPracticeHeader(currentPractice) || getPracticeDescription(currentPractice)) && (
              <div className="mb-3">
                {getPracticeHeader(currentPractice) && (
                  <h2 className="text-lg sm:text-xl font-semibold text-darkBrown text-center">
                    {getPracticeHeader(currentPractice)}
                  </h2>
                )}
                {getPracticeDescription(currentPractice) && (
                  <p className="mt-1.5 text-sm text-stone-600 whitespace-pre-line text-left">
                    {getPracticeDescription(currentPractice)}
                  </p>
                )}
              </div>
            )}
            <VideoDisplay videoUrl={getActivePracticeVideoUrl(currentPractice)} />
            {/* {Boolean(getPracticeAlternativeVideoUrl(currentPractice)) && (
              <div className="mt-3 flex justify-center sm:hidden">
                <div className="w-fit h-7 rounded-full border border-stone-300 bg-stone-100 p-0.5 flex gap-1 items-center">
                  <button
                    onClick={() => setUseAlternativePlayer(false)}
                    className={`rounded-full h-6 px-3 text-xs leading-none flex items-center transition-colors ${
                      !useAlternativePlayer
                        ? "bg-white text-stone-900 shadow-sm"
                        : "text-stone-600 hover:text-stone-900"
                    }`}
                  >
                    {`${ConstructorTrans[language].player} 1`}
                  </button>
                  <button
                    onClick={() => setUseAlternativePlayer(true)}
                    className={`rounded-full h-6 px-3 text-xs leading-none flex items-center transition-colors ${
                      useAlternativePlayer
                        ? "bg-white text-stone-900 shadow-sm"
                        : "text-stone-600 hover:text-stone-900"
                    }`}
                  >
                    {`${ConstructorTrans[language].player} 2`}
                  </button>
                </div>
              </div>
            )} */}
            <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between text-xs md:text-sm">
              {/* {Boolean(getPracticeAlternativeVideoUrl(currentPractice)) && (
                <div className="hidden sm:flex">
                  <div className="w-fit h-7 rounded-full border border-stone-300 bg-stone-100 p-0.5 flex gap-1 items-center">
                    <button
                      onClick={() => setUseAlternativePlayer(false)}
                      className={`rounded-full h-6 px-3 text-xs leading-none flex items-center transition-colors ${
                        !useAlternativePlayer
                          ? "bg-white text-stone-900 shadow-sm"
                          : "text-stone-600 hover:text-stone-900"
                      }`}
                    >
                      {`${ConstructorTrans[language].player} 1`}
                    </button>
                    <button
                      onClick={() => setUseAlternativePlayer(true)}
                      className={`rounded-full h-6 px-3 text-xs leading-none flex items-center transition-colors ${
                        useAlternativePlayer
                          ? "bg-white text-stone-900 shadow-sm"
                          : "text-stone-600 hover:text-stone-900"
                      }`}
                    >
                      {`${ConstructorTrans[language].player} 2`}
                    </button>
                  </div>
                </div>
              )} */}
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 sm:ml-auto">
                <SaveForLaterButton
                  isSaved={selectionStates.isSaved}
                  toggleSave={() => handleRestrictedAction(toggleSaved)}
                  variant="elevated"
                />
                <MarkAsCompleteButton
                  isComplete={selectionStates.isComplete}
                  toggleComplete={() => handleRestrictedAction(toggleComplete)}
                  variant="elevated"
                />
              </div>
            </div>
          </div>
        </PopupModal>
      )}
    </main>
  );
};

export default LibraryCatalogPage;
